import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OnlineStateService } from '@services/online-state.service';
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { concat, distinctUntilChanged, map, Observable, of, switchMap, timer } from 'rxjs';

type OfflineToastState = 'online' | 'offline' | 'restored' | 'closing';

@Component({
  selector: 'offline-toast',
  templateUrl: './offline-toast.component.html',
  styleUrls: ['./offline-toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    TranslateModule,
  ]
})
export class OfflineToastComponent {
  private readonly onlineStateService = inject(OnlineStateService);

  protected readonly state = signal<OfflineToastState>('online');
  protected readonly collapsed = signal(false);
  protected readonly entering = signal(false);

  constructor() {
    this.onlineStateService.isOffline$.pipe(
      distinctUntilChanged(),
      switchMap((isOffline: boolean): Observable<OfflineToastState> => {
        if (isOffline) return of('offline');
        if (this.state() !== 'offline') return of('online');

        return concat(
          of<OfflineToastState>('restored'),
          timer(2000).pipe(map((): OfflineToastState => 'closing')),
          timer(320).pipe(map((): OfflineToastState => 'online')),
        );
      }),
      takeUntilDestroyed(),
    ).subscribe((state: OfflineToastState): void => {
      if (state === 'offline') {
        this.entering.set(this.state() === 'online');
        this.collapsed.set(false);
      }
      if (state === 'online') {
        this.entering.set(false);
      }

      this.state.set(state);
    });
  }

  protected toggleCollapsed(): void {
    if (this.state() === 'closing') return;

    this.entering.set(false);
    this.collapsed.update((collapsed: boolean): boolean => !collapsed);
  }

  protected onEnterAnimationEnd(): void {
    this.entering.set(false);
  }
}
